import React, { useState, useContext, useEffect } from "react";
import { useSpring, animated, easings, useSprings } from "@react-spring/web";
import PropTypes from "prop-types";
import styles from "./AchievementCongratulations.module.scss";
import MainButton from "../MainButton/MainButton.jsx";

import starImage from "./star.png";

import closeIcon from "./close.svg";
import { LanguageContext } from "../../contexts/LanguageContext.jsx";

/**
 * const size = Math.round(Math.random() * 100 + 10); // Random size between 10 and 60, rounded
 * const top = Math.round(Math.random() * 50 + 25); // Random top position between 25% and 75%, rounded
 * const left = Math.round(Math.random() * 50 + 25); // Random left position between 25% and 75%, rounded
 * const scale = Math.random() * 0.5 + 0.5; // Random scale between 0.5 and 1
 */

const starData = [
  { size: 120, top: 25, left: 29, scale: 0.67 },
  { size: 150, top: 35, left: 76, scale: 0.71 },
  { size: 60, top: 32, left: 43, scale: 0.83 },
  { size: 94, top: 69, left: 80, scale: 0.65 },
  { size: 150, top: 37, left: 22, scale: 0.99 },
  { size: 200, top: 77, left: 22, scale: 0.83 },
  { size: 71, top: 72, left: 40, scale: 0.83 },
  { size: 60, top: 75, left: 63, scale: 0.54 },
];

const AchievementCongratulations = ({ onGiftReceive, onClose }) => {
  const { interfaceText } = useContext(LanguageContext);
  const [showH1, setShowH1] = useState(false);
  const [showH2, setShowH2] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showPositionedStars, setShowPositionedStars] = useState(false);

  const overlayAnimation = useSpring({
    from: { opacity: 0.5 },
    to: { opacity: 1 },
    config: { duration: 1000, easing: easings.easeOutQuart },
    onRest: () => setShowH1(true),
  });

  const h1Animation = useSpring({
    from: { top: "110%", transform: "translate(-50%, -50%) scale(0.1)" },
    to: showH1
      ? { top: "50%", transform: "translate(-50%, -50%) scale(1)" }
      : {},
    config: { duration: 1000, easing: easings.easeOutBack },
    onRest: () => setShowH2(true),
  });

  const h2Animation = useSpring({
    from: { opacity: 0 },
    to: showH2 ? { opacity: 1 } : {},
    config: { duration: 1000 },
    onRest: () => setShowPositionedStars(true),
  });

  const buttonAnimation = useSpring({
    from: { opacity: 0 },
    to: showButton ? { opacity: 1 } : {},
    config: { duration: 500 },
  });

  const [springs] = useSprings(starData.length, (index) => ({
    delay: index * 150,
    from: { opacity: 0.65 },
    to: { opacity: 1 },
    config: {
      duration: Math.random() * 1000 + 1000,
      easing: easings.easeInOutSine,
    },
    loop: { reverse: true },
  }));

  const [starPositionAnimation] = useSprings(
    starData.length,
    (index) => ({
      from: { top: "50%", left: "50%" },
      to: async (next) => {
        if (showPositionedStars) {
          await next({
            top: `${starData[index].top}%`,
            left: `${starData[index].left}%`,
          });
          if (index === starData.length - 1) {
            setShowButton(true);
          }
        }
      },
      config: { duration: 1000, easing: easings.easeOutBack },
    }),
    [showPositionedStars],
  );

  const generateStarsFromData = () => {
    return starData.map((star, index) => {
      return (
        <animated.img
          key={index}
          src={starImage}
          alt="Star"
          className={styles.star}
          style={{
            ...springs[index],
            ...starPositionAnimation[index],
            // position: "absolute",
            width: `${star.size}px`,
            height: `${star.size}px`,
            transform: `translate(-50%, -50%) scale(${star.scale})`,
          }}
        />
      );
    });
  };

  return (
    <div className={styles.overlay}>
      {showPositionedStars && generateStarsFromData()}
      <animated.div
        className={styles.background}
        style={overlayAnimation}
      ></animated.div>
      {showH1 && (
        <>
          <animated.div className={styles.scoreContainer} style={h1Animation}>
            <h1 className={styles.score}>10/10</h1>
            {!showH2 && <h2 className={styles.congratulations}>&nbsp;</h2>}
            {showH2 && (
              <animated.h2
                className={styles.congratulations}
                style={h2Animation}
              >
                {interfaceText.gift.congratulations}
              </animated.h2>
            )}
          </animated.div>

          {showButton && (
            <animated.div
              className={styles.giftButtonContainer}
              style={buttonAnimation}
            >
              <MainButton onClick={onGiftReceive} size={"large"}>
                {interfaceText.gift.yourGift}
              </MainButton>
            </animated.div>
          )}
        </>
      )}

      <button className={styles.closeButton} onClick={onClose}>
        <img src={closeIcon} alt="Close" />
      </button>
    </div>
  );
};

AchievementCongratulations.propTypes = {
  onGiftReceive: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AchievementCongratulations;
